import { useEffect, useState, type ChangeEvent } from 'react';
import { MdAutorenew, MdDragIndicator, MdMoreVert } from 'react-icons/md';
import clsx from 'clsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import MultiSelect from '@/components/MultiSelect';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { AgentApiJobChangeProcessBoardsItem } from '@/types/serializers';
import useFetchActiveUsers from '../../../api/useFetchActiveUsers';
import usePostJobChangeProcessBoardsMoveBoard from '../../api/usePostJobChangeProcessBoardsMoveBoard';
import { showErrorToast } from '../../lib/toast';

// 「未設定」を表す担当者ID。BE 側で -1 を「担当者未設定」として扱う
const UNASSIGNED_USER_ID = -1;

function SortableBoardItem({ board }: { board: AgentApiJobChangeProcessBoardsItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: board.id,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    zIndex: isDragging ? 999 : undefined,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#fff' : undefined,
    cursor: 'grab',
  } as const;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx('flex h-xxl items-center gap-1 px-3', isDragging && 'bg-gray-50')}
      {...attributes}
      {...listeners}
    >
      <div className="cursor-move">
        <MdDragIndicator className="size-icon-xs" />
      </div>
      <span
        className={clsx(
          'flex h-lg items-center justify-center rounded-full px-3 text-xs font-bold',
          board.color_type === 'gray' && 'bg-[#EAEAEA]',
          board.color_type === 'green' && 'bg-[#D7F4E0]',
          board.color_type === 'blue' && 'bg-[#D6EEFF] text-[#0E295D]',
          board.color_type === 'yellow_green' && 'bg-[#FAFFE6]',
          board.color_type === 'yellow' && 'bg-[#FFFEE7]',
          board.color_type === 'orange' && 'bg-[#FFEED3] text-[#461304]',
          board.color_type === 'red' && 'bg-[#FDD] text-[#500000]',
          board.color_type === 'pink' && 'bg-[#FEE4FF] text-[#5C0059]',
          board.color_type === 'purple' && 'bg-[#EEE9FE] text-[#320F66]'
        )}
      >
        {board.title}
      </span>
    </div>
  );
}

type Props = {
  setScreenType: (value: 'list' | 'board') => void;
  keyword: string;
  organizationUserIds: number[];
  handleKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  handleOrganizationUserIds: (ids: number[]) => void;
  onCreateBoard: () => Promise<void>;
  isCreatingBoard?: boolean;
  boards: AgentApiJobChangeProcessBoardsItem[];
  boardsRefetch: (options?: { resetPages?: boolean }) => Promise<unknown>;
};

export default function JobChangeProcessBoardSearch({
  setScreenType,
  keyword,
  organizationUserIds,
  handleKeyword,
  handleOrganizationUserIds,
  onCreateBoard,
  isCreatingBoard = false,
  boards,
  boardsRefetch,
}: Props) {
  const { data: activeUsersData } = useFetchActiveUsers();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [boardItems, setBoardItems] = useState<AgentApiJobChangeProcessBoardsItem[]>(boards);
  const { mutateAsync: moveBoard } = usePostJobChangeProcessBoardsMoveBoard();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setBoardItems(boards);
  }, [boards]);

  const organizationUserOptions = [
    { id: UNASSIGNED_USER_ID, name: '未設定' },
    ...(activeUsersData
      ? activeUsersData.records.map((a) => ({
          id: a.id,
          name: a.name,
        }))
      : []),
  ];

  const handleCreateBoard = async () => {
    setIsMoreMenuOpen(false);
    await onCreateBoard();
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = boardItems.findIndex((board) => board.id === Number(active.id));
    const newIndex = boardItems.findIndex((board) => board.id === Number(over.id));

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newBoardItems = arrayMove(boardItems, oldIndex, newIndex);
    setBoardItems(newBoardItems);

    try {
      await moveBoard({
        targetBoardId: Number(active.id),
        order_number: Number(newIndex),
      });

      await boardsRefetch();
    } catch (error) {
      showErrorToast('ボードの並べ替えに失敗しました');
      setBoardItems(boards);
    }
  };

  return (
    <div className="sticky top-15 z-20 flex items-end gap-md bg-gray px-lg py-sm">
      <div className="flex w-[522px] flex-col gap-xs">
        <label htmlFor="board-keyword" className="text-heading-xxs">
          フリーワード
        </label>
        <Input
          id="board-keyword"
          search
          className="w-full"
          autoComplete="off"
          placeholder="テキスト"
          defaultValue={keyword}
          onChange={handleKeyword}
        />
      </div>
      <div className="flex w-[196px] shrink-0 flex-col gap-xs">
        <label className="text-heading-xxs">担当者</label>
        <MultiSelect
          className="w-full"
          options={organizationUserOptions}
          value={organizationUserIds}
          onChange={handleOrganizationUserIds}
          placeholder="担当者を選択"
          matchTriggerWidth
        />
      </div>
      <div className="ml-auto flex gap-xs">
        <Button type="button" color="secondary" size="md" onClick={() => setScreenType('list')}>
          <MdAutorenew className="size-icon-xs" />
          リストビュー
        </Button>
        <Popover open={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
          <PopoverTrigger asChild>
            <Button color="secondary" size="md" asChild iconOnly>
              <div className="size-[42px]">
                <MdMoreVert className="size-icon-xs" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[178px] rounded-sm bg-white p-0">
            <div className="flex flex-col">
              <button
                type="button"
                className="flex h-xxl items-center border-0 bg-transparent px-sm text-body-sm hover:bg-gray_darken disabled:opacity-50"
                onClick={handleCreateBoard}
                disabled={isCreatingBoard}
              >
                ボードを追加
              </button>
              <Popover open={isSortMenuOpen} onOpenChange={setIsSortMenuOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex h-xxl items-center border-0 bg-transparent px-sm text-body-sm hover:bg-gray_darken"
                  >
                    ボードの並べ替え
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="left"
                  className="w-[160px] rounded-sm bg-white p-0"
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="flex flex-col">
                      <SortableContext
                        items={boardItems.map((board) => board.id)}
                        strategy={verticalListSortingStrategy}
                        key={boardItems.map((board) => board.id).join(',')}
                      >
                        {boardItems.map((board) => (
                          <SortableBoardItem key={board.id} board={board} />
                        ))}
                      </SortableContext>
                    </div>
                  </DndContext>
                </PopoverContent>
              </Popover>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
